import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft, Upload } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/leads/import')({
  component: ImportLeadsPage,
})

// Lead fields the backend importer knows how to ingest. Aligned with
// `importLeads` in `apps/backend/src/lib/crm/csv-import.ts`.
// Labels resolved at render time via useTranslations.
const LEAD_FIELD_KEYS = [
  { value: '__skip__', labelKey: 'crm.common.optional' },
  { value: 'firstName', labelKey: 'crm.lead.firstName' },
  { value: 'lastName', labelKey: 'crm.lead.lastName' },
  { value: 'email', labelKey: 'crm.lead.email' },
  { value: 'phone', labelKey: 'crm.lead.phone' },
  { value: 'companyName', labelKey: 'crm.lead.company' },
  { value: 'jobTitle', labelKey: 'crm.lead.jobTitle' },
] as const

type ImportResult = {
  inserted: number
  failed: Array<{ lineNumber: number; error: string }>
}

/**
 * Parse the first line of a CSV into column headers. We do a
 * minimally-correct comma split that respects double-quoted fields.
 * This is intentionally simple — the backend's csv parser handles the
 * real import; this only needs enough precision to surface headers for
 * the column mapper UI.
 */
function parseHeaderRow(text: string): string[] {
  const line = text.split(/\r?\n/)[0] ?? ''
  const out: string[] = []
  let current = ''
  let inQuotes = false
  for (const ch of line) {
    if (ch === '"') {
      inQuotes = !inQuotes
      continue
    }
    if (ch === ',' && !inQuotes) {
      out.push(current.trim())
      current = ''
      continue
    }
    current += ch
  }
  out.push(current.trim())
  return out
}

function toBase64(text: string): string {
  // btoa handles ASCII; for safety we first UTF-8 encode.
  if (typeof TextEncoder !== 'undefined' && typeof btoa !== 'undefined') {
    const bytes = new TextEncoder().encode(text)
    let binary = ''
    for (const b of bytes) binary += String.fromCharCode(b)
    return btoa(binary)
  }
  return btoa(text)
}

function ImportLeadsPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const [csvText, setCsvText] = useState<string>('')
  const [headers, setHeaders] = useState<string[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [result, setResult] = useState<ImportResult | null>(null)

  const importMutation = useMutation(
    orpc.crm.leads.importCsv.mutationOptions({
      onSuccess: (res) => {
        setResult(res as ImportResult)
        toast.success(
          `${(res as ImportResult).inserted} ${t('crm.lead.entityPlural').toLowerCase()}`,
        )
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const text = await file.text()
    setCsvText(text)
    const hdrs = parseHeaderRow(text)
    setHeaders(hdrs)
    // Best-effort auto-map: header name lowercased + stripped of spaces.
    const auto: Record<string, string> = {}
    for (const h of hdrs) {
      const lower = h.toLowerCase().replace(/[^a-z]/g, '')
      if (lower.includes('first')) auto[h] = 'firstName'
      else if (lower.includes('last')) auto[h] = 'lastName'
      else if (lower.includes('email')) auto[h] = 'email'
      else if (lower.includes('phone')) auto[h] = 'phone'
      else if (lower.includes('company') || lower.includes('organization')) auto[h] = 'companyName'
      else if (lower.includes('title') || lower.includes('position')) auto[h] = 'jobTitle'
      else auto[h] = '__skip__'
    }
    setMapping(auto)
    setResult(null)
  }

  const handleImport = () => {
    if (!csvText) {
      toast.error(t('crm.lead.importCsv'))
      return
    }
    // Strip the placeholder "__skip__" entries before sending.
    const cleaned: Record<string, string> = {}
    for (const [src, target] of Object.entries(mapping)) {
      if (target && target !== '__skip__') cleaned[src] = target
    }
    if (Object.keys(cleaned).length === 0) {
      toast.error(t('crm.common.required'))
      return
    }
    importMutation.mutate({ csvBase64: toBase64(csvText), mapping: cleaned })
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/crm/leads' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('crm.common.cancel')}
        </Button>
        <h1 className="text-2xl font-bold">{t('crm.lead.importCsv')}</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>1. {t('crm.lead.importCsv')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
        </CardContent>
      </Card>

      {headers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>2. {t('crm.common.edit')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('navigation.account')}</TableHead>
                  <TableHead>{t('crm.common.convert')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {headers.map((h) => (
                  <TableRow key={h}>
                    <TableCell className="font-mono text-sm">{h}</TableCell>
                    <TableCell>
                      <Select
                        value={mapping[h] ?? '__skip__'}
                        onValueChange={(v) => setMapping((m) => ({ ...m, [h]: v }))}
                      >
                        <SelectTrigger className="w-64">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_FIELD_KEYS.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {t(f.labelKey)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="pt-4 flex justify-end">
              <Button onClick={handleImport} disabled={importMutation.isPending}>
                <Upload className="h-4 w-4 mr-2" />
                {importMutation.isPending ? t('crm.common.saving') : t('crm.lead.importCsv')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card>
          <CardHeader>
            <CardTitle>3. {t('crm.activity.outcome')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm">
              <span className="font-medium">{result.inserted}</span>{' '}
              {t('crm.lead.entityPlural').toLowerCase()}
            </div>
            {result.failed.length > 0 ? (
              <div>
                <div className="text-sm font-medium mb-1">
                  {result.failed.length} {t('crm.common.noRecords')}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">{t('crm.common.createdAt')}</TableHead>
                      <TableHead>{t('crm.common.status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.failed.map((f) => (
                      <TableRow key={f.lineNumber}>
                        <TableCell>{f.lineNumber}</TableCell>
                        <TableCell className="text-sm text-destructive">{f.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">{t('crm.common.noRecords')}</div>
            )}
            <div className="pt-2">
              <Button variant="outline" onClick={() => navigate({ to: '/crm/leads' })}>
                {t('crm.common.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
